import React, { useState } from "react";

import { Box, TextField, Typography, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { GiphyFetch } from "@giphy/js-fetch-api";
import { Grid } from "@giphy/react-components";
import type { IGif } from "@giphy/js-types";

import { GifOverlay } from "../../types";

const GIPHY_API_KEY = process.env.REACT_APP_GIPHY_API_KEY || ""; // Set in .env

const gf = new GiphyFetch(GIPHY_API_KEY);

interface GifPickerProps {
  videoDurationMs: number;
  currentTimeMs: number;
  onAddGif: (gif: GifOverlay) => void;
}

export default function GifPicker({
  videoDurationMs,
  currentTimeMs,
  onAddGif,
}: GifPickerProps) {
  const [searchTerm, setSearchTerm] = useState("");

  const fetchGifs = (offset: number) => {
    if (searchTerm.trim()) {
      return gf.search(searchTerm, { offset, limit: 10 });
    }
    return gf.trending({ offset, limit: 10 });
  };

  const handleGifClick = (gif: IGif, e: React.SyntheticEvent) => {
    e.preventDefault();
    const overlay: GifOverlay = {
      id: crypto.randomUUID(),
      giphyId: gif.id.toString(),
      title: gif.title || "GIF",
      url: gif.images.fixed_height_small.url,
      originalUrl: gif.images.original.url,
      x: 35, // center-ish
      y: 35,
      width: 30,
      height: 30,
      startMs: currentTimeMs,
      endMs: videoDurationMs,
    };
    onAddGif(overlay);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <TextField
        size="small"
        placeholder="Search GIFs..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        fullWidth
      />

      <Box
        sx={{
          maxHeight: 400,
          overflowY: "auto",
          borderRadius: 1,
          border: "1px solid #eee",
        }}
      >
        <Grid
          key={searchTerm}
          width={360}
          columns={3}
          fetchGifs={fetchGifs}
          onGifClick={handleGifClick}
          noLink
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          opacity: 0.6,
          mt: -1,
        }}
      >
        <Typography variant="caption">Powered by GIPHY</Typography>
      </Box>
    </Box>
  );
}
